/**
 * Product card image carousel.
 *
 * Desktop drives the track with a transform: hovering the card advances to the second
 * image, and the arrows step through the rest. Mobile leaves the track as a native
 * scroll-snap container so swiping is the browser's job, and only mirrors the scroll
 * position onto the pagination dots.
 *
 * The slide list is recalculated on every swatch change, because card-swatch.js hides the
 * hover slide for variants that have no hover_image and swaps in the variant's own images.
 *
 * The mobile swipe is switched off for cards inside a horizontally scrolling section (the
 * homepage rows, related and complementary products). Two nested scroll containers on the
 * same axis fight each other, and the card's track wins the gesture because the image covers
 * most of the card, making the row itself hard to scroll. On mobile those cards navigate with
 * the pagination dots only; desktop keeps hover and clicks. PLP grids do not scroll
 * horizontally, so they are unaffected.
 */
// `var` declarations: the file is included once per section, and a re-declared const/class throws.
var CARD_CAROUSEL_DESKTOP = '(min-width: 750px)';

var CardMediaCarousel = class CardMediaCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelector('[data-carousel-dots]');
    this.dotsList = this.querySelector('[data-carousel-dots-list]');
    this.index = 0;
    this.desktopQuery = window.matchMedia(CARD_CAROUSEL_DESKTOP);
    this.toggleAttribute('data-carousel-nested', Boolean(this.closest('slider-component, product-recommendations, .swiper')));

    this.onScroll = this.onScroll.bind(this);
    this.refresh = this.refresh.bind(this);

    this.prevButton?.addEventListener('click', (event) => this.onArrow(event, -1));
    this.nextButton?.addEventListener('click', (event) => this.onArrow(event, 1));

    this.addEventListener('mouseenter', () => {
      if (this.isDesktop() && this.slides.length > 1) this.goTo(1);
    });
    this.addEventListener('mouseleave', () => {
      if (this.isDesktop()) this.goTo(0);
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.desktopQuery.addEventListener('change', this.refresh);

    // card-swatch.js swaps the variant's slides, so rebuild and reset afterwards.
    this.closest('.card-wrapper')?.addEventListener('card:variant-change', () => {
      this.refresh();
      this.goTo(0, { instant: true });
    });

    this.refresh();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.desktopQuery?.removeEventListener('change', this.refresh);
    this.observer?.disconnect();
  }

  isInsideHorizontalScroller() {
    let el = this.parentElement;
    while (el && el !== document.body) {
      const overflowX = getComputedStyle(el).overflowX;
      if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth + 4) return true;
      el = el.parentElement;
    }
    return false;
  }

  /** No point paying for images a card cannot reach. */
  maybeHydrate() {
    if (this.hydrated) return;
    this.hydrated = true;
    this.observeForHydration();
  }

  /**
   * Slides past the first sit outside the track's overflow, where the browser's own lazy
   * loading is unreliable: measured in Chrome, a fourth slide never loaded at all. Flip every
   * slide to eager once the card nears the viewport, which still keeps images for cards
   * further down the page off the initial load.
   */
  observeForHydration() {
    const hydrate = () => {
      this.querySelectorAll('[data-carousel-slide]').forEach((slide) => {
        slide.loading = 'eager';
      });
    };

    if (!('IntersectionObserver' in window)) {
      hydrate();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        hydrate();
        this.observer.disconnect();
        this.observer = null;
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  isDesktop() {
    return this.desktopQuery.matches;
  }

  get allSlides() {
    return Array.from(this.querySelectorAll('[data-carousel-slide]'));
  }

  /** A slide counts only if it has a real source and has not been hidden for this variant. */
  static isUsable(slide) {
    return Boolean(slide.getAttribute('src')) && slide.dataset.failed !== 'true';
  }

  get slides() {
    return this.allSlides.filter((slide) => CardMediaCarousel.isUsable(slide) && !slide.hidden);
  }

  /**
   * Hiding has to happen in the DOM, not just in this filter: a slide with no src is still a
   * full-width flex item, and leaving it laid out is what produces a blank frame.
   */
  syncSlideVisibility() {
    this.allSlides.forEach((slide) => {
      const usable = CardMediaCarousel.isUsable(slide);
      slide.hidden = !usable;
      slide.style.display = usable ? '' : 'none';

      if (usable && !slide.dataset.errorBound) {
        slide.dataset.errorBound = 'true';
        slide.addEventListener('error', () => {
          slide.dataset.failed = 'true';
          this.refresh();
        });
      }
    });
  }

  refresh() {
    this.syncSlideVisibility();

    this.swipeDisabled = !this.isDesktop() && this.isInsideHorizontalScroller();
    this.toggleAttribute('data-carousel-no-swipe', this.swipeDisabled);

    const count = this.slides.length;
    this.toggleAttribute('data-carousel-enabled', count > 1);
    this.buildDots(count);
    this.maybeHydrate();

    if (this.index > count - 1) this.index = 0;
    this.goTo(this.index, { instant: true });
  }

  buildDots(count) {
    if (!this.dots || !this.dotsList) return;

    this.dots.hidden = count < 2;
    if (count < 2) {
      this.dotsList.replaceChildren();
      return;
    }
    if (this.dotsList.children.length === count) return;

    const template = this.dots.dataset.dotLabel || '%INDEX%';
    const dots = Array.from({ length: count }, (_, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'slider-counter__link slider-counter__link--dots link';
      button.setAttribute('aria-label', template.replace('%INDEX%', i + 1));
      button.innerHTML = '<span class="dot"></span>';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(i);
      });
      return button;
    });

    this.dotsList.replaceChildren(...dots);
  }

  onArrow(event, direction) {
    // The arrows sit inside the card, which is otherwise one big link.
    event.preventDefault();
    event.stopPropagation();
    this.goTo(this.index + direction);
  }

  goTo(index, { instant = false } = {}) {
    const slides = this.slides;
    if (!slides.length) return;

    this.index = Math.min(Math.max(index, 0), slides.length - 1);

    if (this.isDesktop()) {
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
    } else {
      this.track.scrollTo({
        left: this.track.clientWidth * this.index,
        behavior: instant ? 'auto' : 'smooth',
      });
    }

    this.updateControls(slides.length);
  }

  onScroll() {
    if (this.isDesktop() || this.swipeDisabled || !this.track.clientWidth) return;

    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index === this.index) return;

    this.index = index;
    this.updateControls(this.slides.length);
  }

  updateControls(count) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));

    if (this.prevButton) this.prevButton.hidden = count < 2 || this.index === 0;
    if (this.nextButton) this.nextButton.hidden = count < 2 || this.index >= count - 1;

    Array.from(this.dotsList?.children || []).forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('slider-counter__link--active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
};

if (!customElements.get('card-media-carousel')) {
  customElements.define('card-media-carousel', CardMediaCarousel);
}
